import React from "react";
import "./BoardPreview.css";

type BoardState = {
	display: boolean;
	screen: string;
	video: string;
	text: string;
	textSize: number;
	timerRunning: boolean;
	timerSeconds: number;
	timerAnchor: string | null;
};

type Props = {
	state: BoardState | null;
	/** The theme this board renders with (layout + assets). */
	theme: any;
	fixture: any;
	/** Home club badge, for the home slot. */
	homeBadge?: string;
	maxWidth?: number;
	screenRoute?: (screen: string) => string | null;
};

const RETURN = "Scoreboard";

/**
 * What a board is showing right now, drawn from the same layout coordinates
 * and assets the board itself uses — so moving an element in the editor moves
 * it here too. Info screens fall back to the existing scoreboard web routes.
 */
export class BoardPreview extends React.Component<Props, { tick: number; boxWidth: number }> {
	timer: any;
	observer: ResizeObserver | null = null;
	host = React.createRef<HTMLDivElement>();

	constructor(props: Props) {
		super(props);
		this.state = { tick: 0, boxWidth: 0 };
	}

	componentDidMount() {
		this.timer = setInterval(() => this.setState({ tick: this.state.tick + 1 }), 1000);
		// Scale to whatever space we're given rather than a fixed size, so the
		// board fits its tile at any column width.
		this.observer = new ResizeObserver((entries) => {
			const width = entries[0]?.contentRect?.width || 0;
			if (width && Math.abs(width - this.state.boxWidth) > 1) this.setState({ boxWidth: width });
		});
		if (this.host.current) this.observer.observe(this.host.current);
	}

	componentWillUnmount() {
		clearInterval(this.timer);
		this.observer?.disconnect();
	}

	/** Live clock reconstructed from the anchor, exactly as the board does. */
	clockSeconds() {
		const s = this.props.state;
		if (!s) return 0;
		let seconds = Number(s.timerSeconds) || 0;
		if (s.timerRunning && s.timerAnchor) {
			const elapsed = (Date.now() - Date.parse(s.timerAnchor)) / 1000;
			if (isFinite(elapsed) && elapsed > 0) seconds += elapsed;
		}
		return Math.max(0, Math.floor(seconds));
	}

	clock() {
		const total = this.clockSeconds();
		const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);
		return `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;
	}

	box(el: any, content: React.ReactNode, key: string) {
		if (!el) return null;
		return (
			<div className="bp-box" key={key} style={{ height: el.height, left: el.x, top: el.y, width: el.width }}>
				{content}
			</div>
		);
	}

	text(el: any, content: React.ReactNode, key: string, fallbackColour?: string) {
		if (!el) return null;
		return (
			<div
				className="bp-text-el"
				key={key}
				style={{ color: el.color || fallbackColour || "#FFFFFF", fontSize: el.fontSize, left: el.x, top: el.y }}
			>
				{content}
			</div>
		);
	}

	render() {
		const { state, theme, fixture, homeBadge, screenRoute } = this.props;
		const layout = theme?.layout || {};
		const width = layout.canvasWidth || 576;
		const height = layout.canvasHeight || 432;
		// Fill the width we've been given (capped by maxWidth when one is set).
		const available = this.props.maxWidth
			? Math.min(this.props.maxWidth, this.state.boxWidth || this.props.maxWidth)
			: this.state.boxWidth;
		const scale = available ? available / width : 0;
		const isUrl = (value: string) => /^https?:\/\//.test(value || "");

		const frame = (inner: React.ReactNode, extraClass = "") => (
			<div className={`bp-wrap ${extraClass}`} style={{ height: height * scale }}>
				<div className="bp-canvas" style={{ height, transform: `scale(${scale})`, width }}>{inner}</div>
			</div>
		);

		// The host always renders so the observer has something to measure.
		const shell = (inner: React.ReactNode) => <div className="bp-host" ref={this.host}>{scale ? inner : null}</div>;

		if (!state) return shell(<div className="bp-flat">No state yet</div>);
		if (!state.display) return shell(<div className="bp-flat bp-off">Display off</div>);

		if (state.video && state.video !== RETURN) {
			return shell(isUrl(state.video)
				? frame(<video autoPlay className="bp-fill" loop muted src={state.video} />)
				: <div className="bp-flat">Video: {state.video}</div>);
		}

		if (state.text && state.text !== RETURN) {
			return shell(frame(
				<div className="bp-fill bp-textlayer" style={{ fontSize: (state.textSize || 16) * 4 }}>{state.text}</div>
			));
		}

		if (state.screen && state.screen !== RETURN) {
			if (isUrl(state.screen)) return shell(frame(<img alt="Graphic" className="bp-fill" src={state.screen} />));
			const route = screenRoute?.(state.screen);
			return shell(route
				? frame(<iframe className="bp-frame-embed" src={route} title="Board preview" />)
				: <div className="bp-flat">{state.screen}</div>);
		}

		// The live board, drawn at the configured coordinates.
		const awayBadge = fixture?.team?.badgeUrl;
		const additional = layout.timer && this.clockSeconds() >= ((theme?.minutesPerHalf || 45) * 60);
		return shell(frame(
			<>
				<div className="bp-bg" style={{ backgroundImage: theme?.backgroundUrl ? `url(${theme.backgroundUrl})` : undefined }} />
				{!additional && this.box(layout.leagueLogo, theme?.leagueLogoUrl
					? <img alt="" src={theme.leagueLogoUrl} />
					: null, "league")}
				{this.box(layout.homeBadge, homeBadge ? <img alt="" src={homeBadge} /> : null, "homeBadge")}
				{this.box(layout.awayBadge, awayBadge ? <img alt="" src={awayBadge} /> : null, "awayBadge")}
				{this.text(layout.timer, this.clock(), "timer", theme?.textColor)}
				{this.text(layout.score, `${Number(fixture?.homeScore) || 0}-${Number(fixture?.awayScore) || 0}`, "score", theme?.textColor)}
				<div className="bp-sponsorbar" style={{ backgroundColor: theme?.mainSponsorBg || "#FFFFFF" }} />
				{this.box(layout.mainSponsor, theme?.mainSponsor?.imageLight
					? <img alt="" src={theme.mainSponsor.imageLight} />
					: null, "sponsor")}
			</>
		));
	}
}
